import { useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Modal } from '@shared/ui/Modal';
import { Badge } from '@shared/ui/Badge';
import { Loader } from '@shared/ui/Progress';
import { Button } from '@shared/ui/Button';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import {
  useGetCorrespondenceDetailsQuery,
  useRegenerateCorrespondenceDraftMutation,
  useReturnCorrespondenceMutation,
  useUnreturnCorrespondenceMutation,
  useSetCorrespondenceContractMutation,
  useListStatutProjectsQuery,
  useSetCorrespondenceProjectMutation,
} from '@shared/api';
import { AnalysisBar } from '@shared/ui/AnalysisBar';
import { ContractPicker } from './ContractPicker';
import {
  correspondenceSourceLabel,
  correspondenceStatusLabel,
  deadlineStatusLabel,
} from '@shared/lib/statusLabels';

const DEADLINE_KIND_LABELS: Record<string, string> = {
  claim_response: 'Срок ответа на претензию',
  answer: 'Срок ответа',
  procedural: 'Процессуальный срок',
  work_start: 'Срок начала работ',
  work_end: 'Срок окончания работ',
  warranty: 'Гарантийный срок',
  prolong: 'Пролонгация / расторжение',
  force_majeure_notice: 'Уведомление о форс-мажоре',
  payment: 'Срок оплаты',
  statute_of_limitations: 'Срок исковой давности',
};

const Section = styled.div`
  margin-top: ${({ theme }) => theme.spacing(5)};

  & > h4 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('ru-RU');
}

const Grid = styled.dl`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)};
  margin: 0;
  font-size: 14px;

  & > dt {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
  & > dd {
    margin: 0;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Content = styled.div`
  white-space: pre-wrap;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  max-height: 240px;
  overflow: auto;
`;

const Empty = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-style: italic;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 13px;

  &:last-child {
    border-bottom: none;
  }
`;

const DraftWrap = styled.div`
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(3)};
`;

/** Карточка одного кандидата-договора при неоднозначной авто-привязке. */
const CandidateRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};

  & + & {
    margin-top: ${({ theme }) => theme.spacing(2)};
  }
`;

const CandidateMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  font-size: 13px;
`;

const CandidateMeta = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** Чип привязанного договора со ссылкой на его карточку. */
const LinkedContract = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 13px;
`;

const ContractLink = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.radius.sm};

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const DraftBadge = styled.div`
  display: inline-block;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primarySoft};
  border-radius: ${({ theme }) => theme.radius.pill};
`;

const DraftText = styled.textarea`
  width: 100%;
  min-height: 180px;
  padding: ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
  line-height: 1.5;
  resize: vertical;
  white-space: pre-wrap;
  font-family: ${({ theme }) => theme.font.mono};
`;

const DraftActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const HitlNote = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(2)};
  font-style: italic;
`;

const Classification = styled.pre`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  font-size: 12px;
  overflow: auto;
  max-height: 200px;
  margin: 0;
`;

interface Props {
  correspondenceId: string;
  onClose: () => void;
}

export function CorrespondenceCard({ correspondenceId, onClose }: Props) {
  const { data, isLoading } = useGetCorrespondenceDetailsQuery(correspondenceId);
  const { data: projectsData } = useListStatutProjectsQuery();
  const [setCorrespondenceProject, { isLoading: isSettingProject }] =
    useSetCorrespondenceProjectMutation();
  const [returnToDispatcher, { isLoading: isReturning }] = useReturnCorrespondenceMutation();
  const [unreturnCorrespondence, { isLoading: isUnreturning }] =
    useUnreturnCorrespondenceMutation();
  const [regenerateDraft, { isLoading: isRegenerating }] =
    useRegenerateCorrespondenceDraftMutation();
  const [setCorrespondenceContract, { isLoading: isLinking }] =
    useSetCorrespondenceContractMutation();
  const [reason, setReason] = useState('');
  const [expected, setExpected] = useState('');
  const [draftText, setDraftText] = useState<string>('');
  const [draftEditing, setDraftEditing] = useState(false);

  // Синхронизируем локальное состояние черновика с серверным значением,
  // когда оно меняется (новая генерация, открытие карточки).
  const serverDraft = data?.correspondence.draft_response ?? '';
  if (!draftEditing && serverDraft !== draftText) {
    // Простой sync без useEffect — допустимо для inputControlled-стиля
    queueMicrotask(() => setDraftText(serverDraft));
  }

  async function onRegenerate() {
    setDraftEditing(false);
    await regenerateDraft(correspondenceId).unwrap();
  }

  // Привязать письмо к договору (HITL-выбор из кандидатов или ручной пикер).
  async function onLinkContract(contractId: string) {
    await setCorrespondenceContract({ id: correspondenceId, contractId }).unwrap();
  }

  // Отвязать письмо от договора.
  async function onUnlinkContract() {
    await setCorrespondenceContract({ id: correspondenceId, contractId: null }).unwrap();
  }

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(draftText);
    } catch {
      /* clipboard блокирован — игнор */
    }
  }

  async function onReturn(e: FormEvent) {
    e.preventDefault();
    if (!reason.trim()) return;
    await returnToDispatcher({
      id: correspondenceId,
      reason,
      expected_department: expected || undefined,
    }).unwrap();
    setReason('');
    setExpected('');
    onClose();
  }

  if (isLoading || !data) {
    return (
      <Modal open title="Карточка письма" onClose={onClose}>
        <Loader />
      </Modal>
    );
  }

  const c = data.correspondence;
  const corrProjectId = (c as { project_id?: string | null }).project_id ?? '';
  const statutProjects = projectsData?.items ?? [];

  return (
    <Modal open title="Карточка письма" onClose={onClose}>
      <Grid>
        <dt>Поступило</dt>
        <dd>{new Date(c.incoming_at).toLocaleString('ru-RU')}</dd>
        <dt>Источник</dt>
        <dd>
          <Badge>{correspondenceSourceLabel(c.source)}</Badge>
        </dd>
        <dt>Отправитель</dt>
        <dd>{c.sender ?? '—'}</dd>
        <dt>Тема</dt>
        <dd>{c.subject ?? '—'}</dd>
        <dt>Статус</dt>
        <dd>
          <Badge>{correspondenceStatusLabel(c.status)}</Badge>
        </dd>
        <dt>Возвращено диспетчеру</dt>
        <dd>{c.returned_to_dispatcher ? 'да' : '—'}</dd>
        <dt>Строительный объект (ПТО)</dt>
        <dd>
          <Select
            value={corrProjectId}
            disabled={isSettingProject}
            onChange={(e) => {
              const pid = e.target.value || null;
              void setCorrespondenceProject({ correspondenceId, project_id: pid });
            }}
            style={{ maxWidth: 340 }}
            aria-label="Строительный объект письма"
          >
            <option value="">— не привязан —</option>
            {statutProjects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code ? `${p.code} · ` : ''}
                {p.name}
              </option>
            ))}
          </Select>
        </dd>
      </Grid>

      <Section>
        <h4>Содержание</h4>
        {c.content_text ? (
          <Content>{c.content_text}</Content>
        ) : (
          <Empty>Текстового содержимого нет.</Empty>
        )}
      </Section>

      <Section>
        <h4>Первичная классификация диспетчера</h4>
        {c.dispatcher_classification ? (
          <Classification>{JSON.stringify(c.dispatcher_classification, null, 2)}</Classification>
        ) : (
          <Empty>Диспетчер не передал классификацию.</Empty>
        )}
      </Section>

      <Section>
        <h4>Содержательная классификация агента</h4>
        {c.agent_classification ? (
          <Classification>{JSON.stringify(c.agent_classification, null, 2)}</Classification>
        ) : (
          <AnalysisBar
            title="LLM классифицирует письмо…"
            hint="Определяю тип, применимые нормы, срок ответа и срочность. Обычно 10–30 секунд."
          />
        )}
      </Section>

      <Section>
        <h4>Договор</h4>
        {(() => {
          const link = c.agent_classification?.contract_link;
          // 1) Письмо уже привязано к договору.
          if (c.contract_id) {
            return (
              <>
                <LinkedContract>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <Badge>привязан</Badge>{' '}
                    <ContractLink href={`/agents/statut/contracts/${c.contract_id}`}>
                      Открыть карточку договора →
                    </ContractLink>
                  </span>
                  <Button
                    type="button"
                    $variant="secondary"
                    $size="sm"
                    onClick={onUnlinkContract}
                    disabled={isLinking}
                  >
                    {isLinking ? 'Отвязываем…' : 'Отвязать'}
                  </Button>
                </LinkedContract>
              </>
            );
          }
          // 2) Авто-привязка неоднозначна — несколько кандидатов, выбор юриста.
          if (link?.status === 'ambiguous' && link.candidates && link.candidates.length > 0) {
            return (
              <>
                <Empty style={{ fontStyle: 'normal', marginBottom: 12 }}>
                  Найдено несколько договоров с номером{' '}
                  <strong>{link.number ?? '—'}</strong>. Выберите нужный — он
                  будет привязан к письму.
                </Empty>
                {link.candidates.map((cand) => (
                  <CandidateRow key={cand.contract_id}>
                    <CandidateMain>
                      <span>
                        <strong>№ {cand.contract_number ?? '—'}</strong>
                        {cand.counterparty ? ` · ${cand.counterparty}` : ''}
                      </span>
                      <CandidateMeta>
                        {cand.subject ?? 'без предмета'}
                        {cand.signed_at ? ` · подписан ${fmtDate(cand.signed_at)}` : ''}
                      </CandidateMeta>
                    </CandidateMain>
                    <Button
                      type="button"
                      $size="sm"
                      onClick={() => onLinkContract(cand.contract_id)}
                      disabled={isLinking}
                    >
                      {isLinking ? '…' : 'Привязать'}
                    </Button>
                  </CandidateRow>
                ))}
              </>
            );
          }
          // 3) Не найдено автоматически — ручная привязка через пикер.
          return (
            <>
              <Empty style={{ fontStyle: 'normal', marginBottom: 12 }}>
                {link?.number
                  ? `Договор с номером «${link.number}» не найден автоматически. Привяжите вручную.`
                  : 'Договор не найден автоматически. Привяжите вручную, если письмо относится к договору.'}
              </Empty>
              <ContractPicker
                value={null}
                onSelect={(contractId) => void onLinkContract(contractId)}
                disabled={isLinking}
              />
            </>
          );
        })()}
      </Section>

      <Section>
        <h4>Проект ответа от АО «САТКОМ»</h4>
        {c.draft_response ? (
          <DraftWrap>
            <DraftBadge>Черновик · согласовать с юристом</DraftBadge>
            <DraftText
              value={draftText}
              onChange={(e) => {
                setDraftEditing(true);
                setDraftText(e.target.value);
              }}
            />
            <DraftActions>
              <Button type="button" onClick={onCopy}>
                Скопировать
              </Button>
              <Button type="button" onClick={onRegenerate} disabled={isRegenerating}>
                {isRegenerating ? 'Перегенерация…' : 'Перегенерировать'}
              </Button>
              {c.draft_generated_at && (
                <span>обновлён {new Date(c.draft_generated_at).toLocaleString('ru-RU')}</span>
              )}
            </DraftActions>
            <HitlNote>
              (Human-in-the-loop): любой исходящий ответ контрагенту/госоргану согласует юрист. Это
              автоматически сгенерированный проект — отредактируйте перед отправкой.
            </HitlNote>
          </DraftWrap>
        ) : !data.correspondence.agent_classification ? (
          <AnalysisBar
            title="Ждём классификацию — проект ответа появится автоматически"
            hint="Сначала агент классифицирует письмо, затем готовит черновик ответа от лица АО «САТКОМ»."
          />
        ) : isRegenerating ? (
          <AnalysisBar
            title="LLM пишет проект ответа от лица АО «САТКОМ»…"
            hint="Обычно 10–30 секунд. Это черновик — финальную редакцию утвердит юрист."
          />
        ) : (
          <Empty>
            Проект ответа не сгенерирован (письмо не в компетенции юр.отдела). Можно
            перегенерировать вручную.
            <div style={{ marginTop: 12 }}>
              <Button
                type="button"
                onClick={onRegenerate}
                disabled={isRegenerating || !c.content_text}
              >
                Сгенерировать проект ответа
              </Button>
            </div>
          </Empty>
        )}
      </Section>

      <Section>
        <h4>Сроки ответа ({data.deadlines.length})</h4>
        {data.deadlines.length === 0 ? (
          <Empty>Сроков нет.</Empty>
        ) : (
          data.deadlines.map((d) => {
            const days = Math.round((new Date(d.due_at).getTime() - Date.now()) / 86_400_000);
            return (
              <Row key={d.id}>
                <span>{new Date(d.due_at).toLocaleDateString('ru-RU')}</span>
                <span style={{ flex: 1 }}>
                  <strong>{DEADLINE_KIND_LABELS[d.kind] ?? d.kind}</strong>
                  <span style={{ marginLeft: 8, opacity: 0.7 }}>
                    · {days < 0 ? `просрочено на ${-days} дн.` : `осталось ${days} дн.`}
                  </span>
                </span>
                <Badge>{deadlineStatusLabel(d.status)}</Badge>
              </Row>
            );
          })
        )}
      </Section>

      {data.attachments && data.attachments.length > 0 && (
        <Section>
          <h4>Вложения ({data.attachments.length})</h4>
          {data.attachments.map((a) => (
            <Row key={a.id}>
              <span style={{ flex: 1 }}>{a.filename}</span>
              <span style={{ opacity: 0.7, fontSize: 12 }}>
                {(a.size_bytes / 1024).toFixed(1)} КБ
              </span>
              {a.content_type && (
                <span style={{ opacity: 0.5, fontSize: 12 }}>{a.content_type}</span>
              )}
            </Row>
          ))}
        </Section>
      )}

      {data.linked_gov_request && (
        <Section>
          <h4>Запрос госоргана</h4>
          <Row>
            <Badge>{data.linked_gov_request.authority}</Badge>
            <span style={{ flex: 1 }}>
              <strong>{data.linked_gov_request.subject}</strong>
              {data.linked_gov_request.request_no && (
                <span style={{ marginLeft: 8, opacity: 0.7 }}>
                  · № {data.linked_gov_request.request_no}
                </span>
              )}
            </span>
            <a
              href={`/agents/statut/gov`}
              style={{ fontSize: 13, color: 'var(--osnova-primary, #3b82f6)' }}
            >
              Открыть в реестре →
            </a>
          </Row>
          <Empty style={{ marginTop: 8 }}>
            Автоматически создано в реестре «Госорганы» — там жёсткие сроки, оценка
            правомерности и шаблоны ответов.
          </Empty>
        </Section>
      )}

      {data.linked_claim && (
        <Section>
          <h4>
            {data.linked_claim.claim_type === 'lawsuit' ? 'Иск / суд.документ' : 'Претензия'}
          </h4>
          <Row>
            <Badge>{data.linked_claim.claim_type === 'lawsuit' ? 'иск' : 'претензия'}</Badge>
            <span style={{ flex: 1 }}>
              <strong>{data.linked_claim.counterparty}</strong>
              {data.linked_claim.dispute_type && (
                <span style={{ marginLeft: 8, opacity: 0.7 }}>
                  · {data.linked_claim.dispute_type}
                </span>
              )}
            </span>
            <a
              href={`/agents/statut/claims`}
              style={{ fontSize: 13, color: 'var(--osnova-primary, #3b82f6)' }}
            >
              Открыть в реестре →
            </a>
          </Row>
          <Empty style={{ marginTop: 8 }}>
            Автоматически создано в реестре «Претензии и иски» — там идёт LLM-анализ
            (контраргументы, перерасчёт, проект ответа, СИД).
          </Empty>
        </Section>
      )}

      {data.linked_contract && (
        <Section>
          <h4>Проект договора</h4>
          <Row>
            <Badge>договор</Badge>
            <span style={{ flex: 1 }}>
              <strong>{data.linked_contract.counterparty}</strong>
              {data.linked_contract.subject && (
                <span style={{ marginLeft: 8, opacity: 0.7 }}>
                  · {data.linked_contract.subject}
                </span>
              )}
            </span>
            <a
              href={`/agents/statut/contracts`}
              style={{ fontSize: 13, color: 'var(--osnova-primary, #3b82f6)' }}
            >
              Открыть в реестре →
            </a>
          </Row>
          <Empty style={{ marginTop: 8 }}>
            Автоматически создано в реестре «Договоры» — там идёт правовая экспертиза
            по 7 категориям, проверка с типовой формой и протокол разногласий.
          </Empty>
        </Section>
      )}

      {!c.returned_to_dispatcher && (
        <Section>
          <h4>Вернуть на перемаршрутизацию</h4>
          <form onSubmit={onReturn}>
            <Field>
              <Label htmlFor="expected">Подразделение, которому письмо адресовано</Label>
              <Input
                id="expected"
                value={expected}
                onChange={(e) => setExpected(e.target.value)}
                placeholder="напр., бухгалтерия, отдел кадров"
              />
            </Field>
            <Field>
              <Label htmlFor="reason">Причина возврата *</Label>
              <Input
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="напр., вопрос не относится к компетенции юр.отдела"
                required
              />
            </Field>
            <Button type="submit" disabled={isReturning}>
              Вернуть диспетчеру
            </Button>
          </form>
        </Section>
      )}

      {c.returned_to_dispatcher && (
        <Section>
          <h4>Письмо возвращено</h4>
          <Empty>
            Агент посчитал, что письмо не относится к компетенции юр.отдела. Если это
            ошибка классификатора (например, договор пришёл во вложении и LLM не учла
            это), верните письмо в работу — оно встанет обратно в очередь и будет
            переклассифицировано заново.
          </Empty>
          <div style={{ marginTop: 12 }}>
            <Button
              type="button"
              onClick={async () => {
                try {
                  await unreturnCorrespondence(correspondenceId).unwrap();
                } catch {
                  /* молча — RTK кэш обновится и юрист увидит результат */
                }
              }}
              disabled={isUnreturning}
            >
              {isUnreturning ? 'Возвращаем в работу…' : 'Вернуть в работу'}
            </Button>
          </div>
        </Section>
      )}
    </Modal>
  );
}
